import React, { useEffect, useMemo, useRef, useState } from "react";
import { notifyAccessChanged } from "services/utils/companyAccess";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import {
  CheckCircleRounded,
  ErrorOutlineOutlined,
  LayersRounded,
  SaveOutlined,
  ShieldRounded,
} from "@mui/icons-material";
import { useFetch } from "services/hooks/useFetch";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { specialCharacterRestrict } from "services/constants/patterns";
import { designColors, designFontFamily } from "services/constants/designTokens";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { EmptyState } from "components/CustomComponents/EmptyState";
import {
  GlobalBreadcrumb,
  GlobalFieldLabel,
} from "components/DesignSystem/sharedUiPatterns";
import { roleAndGroupUi } from "../ui";
import { approvalRequiresEdit, updateRolePermission } from "./rolePermissionRules";
import { buildRoleStatusPayload, getRoleStatus, resolveRoleStatusRecord } from "./roleStatus";
import {
  ModuleList,
  PermissionMatrix,
  ProductIcon,
  SearchField,
  SectionHeading,
  getProductSelectedCount,
  isSubmoduleEnabled,
  isSubmoduleSelected,
  roleKitType,
  tealCheckboxSx,
} from "./roleUiKit";

const NAME_MAX = 100;

const fieldSx = {
  ...roleAndGroupUi.textField,
  "& .MuiInputBase-input::placeholder": { color: "#94A3B8", opacity: 1 },
};
const DESCRIPTION_MAX = 250;

const resetSubmodulePermissions = (submodule) => ({
  ...submodule,
  permissions: {
    ...submodule?.permissions,
    viewer: false,
    editor: false,
    first_level_approver: false,
    second_level_approver: false,
  },
});

// product-control/products/summary/ used to reply with module_group_id /
// module_group; the backend now uses product_id / product_name (2026-10-01).
// Both shapes are accepted, and the catalog entry the summary was requested
// for fills any gap, so the rest of the form keys off product_name like the
// role endpoint does.
const normalizeCatalogProduct = (product, catalogEntry) => ({
  ...product,
  product_id:
    product?.product_id ?? product?.module_group_id ?? catalogEntry?.product_id,
  product_name:
    product?.product_name ??
    product?.module_group ??
    product?.module_group_name ??
    catalogEntry?.product_name,
  module_details: (product?.module_details ?? []).map((module) => ({
    ...module,
    submodules: (module?.submodules ?? []).map(resetSubmodulePermissions),
  })),
});

const getModuleSubmoduleCount = (module) => (module?.submodules ?? []).length;
const getSelectedSubmoduleCount = (module) =>
  (module?.submodules ?? []).filter(isSubmoduleEnabled).length;

// edit_applicable / first_level_approver_applicable /
// second_level_approver_applicable are read-only catalog metadata, and the
// API only wants view_applicable at the module level -- all four are
// dropped from every submodule in the request.
const buildSubmodulePayload = (submodule) => {
  const {
    edit_applicable,
    first_level_approver_applicable,
    second_level_approver_applicable,
    view_applicable,
    ...rest
  } = submodule;
  return rest;
};

// A module is sent hidden (view_applicable:false) when its own checkbox was
// turned off or none of its submodules hold a grant.
const buildModulePayload = (module) => {
  const moduleHidden =
    module?.view_applicable === false ||
    (getModuleSubmoduleCount(module) > 0 && getSelectedSubmoduleCount(module) === 0);
  return {
    ...module,
    submodules: (module?.submodules ?? []).map(buildSubmodulePayload),
    ...(moduleHidden ? { view_applicable: false } : {}),
  };
};

// Every catalog module is listed in the form now (not only the ones added
// through the old "Add Module" picker), so only modules that actually hold a
// grant are sent. Leaving a module with nothing selected is the same as the
// old "remove module" action, which dropped it from the payload entirely.
const buildProductsPayload = (products) =>
  (products ?? [])
    .filter((product) => getProductSelectedCount(product) > 0)
    .map((product) => ({
      ...product,
      module_details: (product?.module_details ?? []).map(buildModulePayload),
    }));

const clearPermissions = (submodule) => ({
  ...submodule,
  permissions: {
    ...submodule.permissions,
    viewer: false,
    editor: false,
    first_level_approver: false,
    second_level_approver: false,
  },
});

// variant "panel" renders inside GlobalSidePanel over the Roles list (Add
// Role and Edit Role), whose expand button swaps to the full view with the
// same two-column layout; "page" is a standalone full screen (unused now).
export const RolePermissionForm = ({
  mode = "edit",
  variant = "page",
  setScreenNavigation,
  roleDetails,
  onClose,
  onSaved,
  initialExpanded = false,
  // Reports every switch between side panel and full view, so the View
  // panel can come back in the same mode.
  onExpandedChange,
}) => {
  const isEdit = mode === "edit";
  const isPanel = variant === "panel";
  const [expanded, setExpanded] = useState(Boolean(initialExpanded));
  useEffect(() => {
    onExpandedChange?.(expanded);
    // onExpandedChange is a plain setter from the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);
  const twoColumn = !isPanel || expanded;
  const { domain, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  const canEdit = checkEditorPermission(
    userScreenAccess?.module_details,
    "Roles & User Group",
    "Roles",
  );
  // Roles are schema-wide, not per company: none of the role/catalog
  // endpoints take a company_id, so the top-bar company doesn't matter here.
  const { result: roleData, loading: roleLoading } = useFetch(
    isEdit ? `${domain}product-control/role/?role_id=${roleDetails?.role_id}` : "",
  );
  const { result: rawCatalog, loading: catalogLoading } = useFetch(
    `${domain}product-control/products/`,
  );
  const catalog = useMemo(
    () =>
      // product-control/products/ returns [{ product_id, product_name }]
      // (confirmed 2026-10-01); the old module_group_id/module_group_name
      // keys are only a fallback. Reading the old keys alone made every id
      // undefined, so summary/ was called with module_group_id=undefined.
      (Array.isArray(rawCatalog) ? rawCatalog : [])
        .map((product) => ({
          ...product,
          product_id: product?.product_id ?? product?.module_group_id,
          product_name: product?.product_name ?? product?.module_group_name,
        }))
        .filter((product) => product.product_id != null),
    [rawCatalog],
  );

  const [products, setProducts] = useState([]);
  const [initialProducts, setInitialProducts] = useState([]);
  const [initialised, setInitialised] = useState(false);
  const [catalogDetailsLoading, setCatalogDetailsLoading] = useState(false);
  const [activeProduct, setActiveProduct] = useState("");
  const [roleName, setRoleName] = useState(isEdit ? roleDetails?.role_name ?? "" : "");
  const [description, setDescription] = useState(isEdit ? roleDetails?.description ?? "" : "");
  const [status, setStatus] = useState(isEdit ? getRoleStatus(roleDetails) : "Active");
  const [initialStatus, setInitialStatus] = useState(isEdit ? getRoleStatus(roleDetails) : "Active");
  const [nameError, setNameError] = useState("");
  const [moduleError, setModuleError] = useState("");
  const [search, setSearch] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const saveInProgressRef = useRef(false);

  const dataReady = !catalogLoading && (!isEdit || !roleLoading);

  // Seed the form once: the role's own modules come from the role endpoint,
  // every other catalog module is fetched from products/summary/ with all
  // permissions cleared, so the left list shows the full catalog with real
  // 0/N counts the way the design does.
  useEffect(() => {
    if (!dataReady || initialised) return;
    setInitialised(true);
    const nextStatus = isEdit ? getRoleStatus(resolveRoleStatusRecord(roleData, roleDetails)) : "Active";
    setStatus(nextStatus);
    setInitialStatus(nextStatus);
    const roleProducts = isEdit ? roleData?.products ?? [] : [];
    setProducts(roleProducts);
    setInitialProducts(roleProducts);
    const firstSelected =
      roleProducts.find((product) => getProductSelectedCount(product) > 0) || roleProducts[0];
    setActiveProduct(firstSelected?.product_name || catalog[0]?.product_name || "");

    const missing = catalog.filter(
      (product) => !roleProducts.some((item) => item?.product_name === product?.product_name),
    );
    if (!missing.length) return;
    setCatalogDetailsLoading(true);
    Promise.all(
      missing.map((product) =>
        customGetAPI(
          `${domain}product-control/products/summary/?module_group_id=${product?.product_id}`,
        )
          .then((res) =>
            res?.code ? normalizeCatalogProduct(res?.responseDetails, product) : null,
          )
          .catch(() => null),
      ),
    ).then((details) => {
      const loaded = details.filter((item) => item?.product_name);
      setProducts((prev) => [
        ...prev,
        ...loaded.filter(
          (item) => !prev.some((existing) => existing?.product_name === item?.product_name),
        ),
      ]);
      setActiveProduct((prev) => prev || loaded[0]?.product_name || "");
      setCatalogDetailsLoading(false);
    });
  }, [catalog, dataReady, domain, initialised, isEdit, roleData, roleDetails]);

  // Catalog order for the left list; role-only modules missing from the
  // catalog keep their place at the end.
  const orderedProducts = useMemo(() => {
    const order = new Map(catalog.map((product, index) => [product?.product_name, index]));
    return [...products].sort(
      (a, b) =>
        (order.get(a?.product_name) ?? Number.MAX_SAFE_INTEGER) -
        (order.get(b?.product_name) ?? Number.MAX_SAFE_INTEGER),
    );
  }, [catalog, products]);

  const selectedProduct = products.find((product) => product?.product_name === activeProduct);

  const updateProduct = (productName, updater) => {
    setProducts((prev) =>
      prev.map((product) =>
        product?.product_name === productName ? updater(product) : product,
      ),
    );
    setModuleError("");
  };

  // Module (product) checkbox: on grants View on every sub-module and
  // clears any leftover hidden flags; off revokes everything.
  const toggleProduct = (productName, value) => {
    setActiveProduct(productName);
    updateProduct(productName, (product) => ({
      ...product,
      module_details: (product?.module_details ?? []).map((module) => ({
        ...module,
        ...(value ? { view_applicable: true } : {}),
        submodules: (module?.submodules ?? []).map((submodule) => ({
          ...submodule,
          ...(value ? { view_applicable: true } : {}),
          permissions: {
            ...submodule.permissions,
            viewer: value,
            editor: false,
            first_level_approver: false,
            second_level_approver: false,
          },
        })),
      })),
    }));
  };

  // Row selection / View checkbox: on grants View (and un-hides the row and
  // its module), off revokes every permission on the row.
  const toggleSubmodule = (moduleName, subModuleName, value) => {
    updateProduct(activeProduct, (product) => ({
      ...product,
      module_details: (product?.module_details ?? []).map((module) => {
        if (module?.module_name !== moduleName) return module;
        return {
          ...module,
          ...(value ? { view_applicable: true } : {}),
          submodules: (module?.submodules ?? []).map((submodule) => {
            if (submodule?.sub_module_name !== subModuleName) return submodule;
            if (!value) return clearPermissions(submodule);
            return {
              ...submodule,
              view_applicable: true,
              permissions: { ...submodule.permissions, viewer: true },
            };
          }),
        };
      }),
    }));
  };

  // Edit/Approve. Granting either on a row that isn't selected yet also
  // grants View, since a sub-module can't be edited or approved without
  // being visible.
  const changePermission = (moduleName, subModuleName, field, value) => {
    updateProduct(activeProduct, (product) => ({
      ...product,
      module_details: (product?.module_details ?? []).map((module) => {
        if (module?.module_name !== moduleName) return module;
        return {
          ...module,
          ...(value ? { view_applicable: true } : {}),
          submodules: (module?.submodules ?? []).map((submodule) => {
            if (submodule?.sub_module_name !== subModuleName) return submodule;
            const permissions = updateRolePermission(
              submodule.permissions,
              field,
              value,
              product?.product_name,
              submodule?.edit_applicable !== false,
            );
            return {
              ...submodule,
              ...(value ? { view_applicable: true } : {}),
              permissions: { ...permissions, viewer: value ? true : permissions.viewer },
            };
          }),
        };
      }),
    }));
  };

  // "Select All" (restored from the pre-redesign screens) grants every
  // permission that applies -- View, Edit and Approve -- on every sub-module
  // of the open module. Approve is offered on every sub-module, whatever
  // first_level_approver_applicable says (2026-10-01, user request), so it is
  // always granted here. "Deselect All" clears the module.
  const isFullyGranted = (submodule) =>
    isSubmoduleSelected(submodule) &&
    Boolean(submodule?.permissions?.viewer) &&
    (submodule?.edit_applicable === false || Boolean(submodule?.permissions?.editor)) &&
    Boolean(submodule?.permissions?.first_level_approver);

  const setAllPermissions = (productName, value) => {
    updateProduct(productName, (product) => ({
      ...product,
      module_details: (product?.module_details ?? []).map((module) => ({
        ...module,
        ...(value ? { view_applicable: true } : {}),
        submodules: (module?.submodules ?? []).map((submodule) =>
          value
            ? {
                ...submodule,
                view_applicable: true,
                permissions: {
                  ...submodule.permissions,
                  viewer: true,
                  editor: submodule?.edit_applicable !== false,
                  first_level_approver: true,
                  second_level_approver: false,
                },
              }
            : clearPermissions(submodule),
        ),
      })),
    }));
  };

  // Edit / Approve header checkboxes: apply that one permission to every
  // sub-module of the open module where it applies, through the same rules
  // as a single click (updateRolePermission). Granting also selects the row
  // (View); revoking leaves the row selected.
  const setColumnPermission = (productName, field, value) => {
    const applies = (submodule) =>
      field === "editor" ? submodule?.edit_applicable !== false : true;
    updateProduct(productName, (product) => ({
      ...product,
      module_details: (product?.module_details ?? []).map((module) => {
        const touched = (module?.submodules ?? []).some(applies);
        return {
          ...module,
          ...(value && touched ? { view_applicable: true } : {}),
          submodules: (module?.submodules ?? []).map((submodule) => {
            if (!applies(submodule)) return submodule;
            const permissions = updateRolePermission(
              submodule.permissions,
              field,
              value,
              productName,
              submodule?.edit_applicable !== false,
            );
            return value
              ? { ...submodule, view_applicable: true, permissions: { ...permissions, viewer: true } }
              : { ...submodule, permissions };
          }),
        };
      }),
    }));
  };

  const payloadProducts = buildProductsPayload(products);
  const hasUnsavedChanges =
    !isEdit ||
    roleName !== (roleDetails?.role_name ?? "") ||
    description !== (roleDetails?.description ?? "") ||
    status !== initialStatus ||
    JSON.stringify(payloadProducts) !== JSON.stringify(buildProductsPayload(initialProducts));

  const nameChanged = !isEdit || roleName.trim() !== (roleDetails?.role_name ?? "").trim();

  const checkNameUnique = async () => {
    if (!roleName.trim() || !nameChanged) return true;
    try {
      const res = await customGetAPI(
        `${domain}unique/role/?name=${encodeURIComponent(roleName.trim())}`,
      );
      if (res?.ok && res?.responseDetails?.exists) {
        setNameError("Role name already exists");
        return false;
      }
      return Boolean(res?.ok);
    } catch (error) {
      showToast("error", error?.message || "Couldn't verify the role name. Please try again.");
      return false;
    }
  };

  const goBack = () => (isPanel ? onClose?.() : setScreenNavigation(""));

  const saveRole = async () => {
    if (saveInProgressRef.current) return;
    if (!roleName.trim()) {
      setNameError("Role name is required");
      return;
    }
    if (payloadProducts.length === 0) {
      setModuleError("Select at least one sub-module for this role.");
      return;
    }
    saveInProgressRef.current = true;
    setIsSaving(true);
    try {
      if (!(await checkNameUnique())) return;
      const body = {
        role_name: roleName.trim(),
        description: description.trim(),
        products: payloadProducts,
        ...(isEdit ? buildRoleStatusPayload(status, resolveRoleStatusRecord(roleData, roleDetails)) : {}),
      };
      const response = isEdit
        ? await customAPI(`${domain}product-control/role/`, "PUT", {
            role_id: roleDetails?.role_id,
            ...body,
          })
        : await customAPI(`${domain}product-control/role/`, "POST", body);
      if (response?.ok) {
        notifyAccessChanged();
        showToast("success", isEdit ? "Role updated." : "New role created.");
        onSaved?.();
        goBack();
      }
    } finally {
      saveInProgressRef.current = false;
      setIsSaving(false);
    }
  };

  if (!dataReady && !isPanel) return <Loader info="Loading..." />;

  const title = isEdit ? `Edit ${roleDetails?.role_name ?? "Role"}` : "Add Role";
  const cardSx = { ...roleAndGroupUi.sectionCard, p: { xs: 2, md: 2.5 } };

  const subtitle = isEdit
    ? "Update role details and configure module permissions."
    : "Enter role details and configure module permissions.";
  const breadcrumbTrail = [
    "Administration",
    { label: "Roles", onClick: goBack },
    isEdit ? "Edit Role" : "Add Role",
  ];

  const searchField = (
    <SearchField
      value={search}
      onChange={setSearch}
      placeholder="Search sub-modules..."
      sx={twoColumn ? {} : { mt: 1.5, width: "100%", minWidth: 0 }}
    />
  );

  const body = (
      <Mui.Stack spacing={2.5}>
        <Mui.Box sx={cardSx}>
          <SectionHeading
            icon={<ShieldRounded />}
            iconColor="#2563EB"
            iconBg="#EFF6FF"
            title="Role Details"
          />
          <Mui.Box sx={{ mt: 2 }}>
            <GlobalFieldLabel>
              Role Name <Mui.Box component="span" sx={{ color: "#DC2626" }}>*</Mui.Box>
            </GlobalFieldLabel>
            <Mui.TextField
              fullWidth
              size="small"
              value={roleName}
              placeholder="Enter role name"
              onChange={(event) => {
                const next = event.target.value;
                if (specialCharacterRestrict.test(next) && next.length <= NAME_MAX) {
                  setRoleName(next);
                  if (nameError) setNameError("");
                }
              }}
              onBlur={checkNameUnique}
              error={Boolean(nameError)}
              helperText={nameError || " "}
              sx={fieldSx}
            />
          </Mui.Box>
          <Mui.Box>
            <GlobalFieldLabel>Description</GlobalFieldLabel>
            <Mui.TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              value={description}
              placeholder="Describe what this role is responsible for"
              onChange={(event) => {
                if (event.target.value.length <= DESCRIPTION_MAX) {
                  setDescription(event.target.value);
                }
              }}
              sx={{
                ...fieldSx,
                "& .MuiOutlinedInput-root": {
                  ...roleAndGroupUi.textField["& .MuiOutlinedInput-root"],
                  alignItems: "flex-start",
                },
              }}
            />
            <Mui.Typography
              sx={{ mt: 0.5, textAlign: "right", fontSize: 12, color: designColors.slate600 }}
            >
              {description.length}/{DESCRIPTION_MAX}
            </Mui.Typography>
          </Mui.Box>
          {isEdit ? <Mui.Box>
            <GlobalFieldLabel>Status</GlobalFieldLabel>
            <Mui.Select
              fullWidth
              size="small"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              disabled={!canEdit || isSaving}
              inputProps={{ "aria-label": "Role status" }}
              sx={fieldSx}
            >
              <Mui.MenuItem value="Active">Active</Mui.MenuItem>
              <Mui.MenuItem value="Inactive">Inactive</Mui.MenuItem>
            </Mui.Select>
          </Mui.Box> : null}
        </Mui.Box>

        <Mui.Box sx={{ ...roleAndGroupUi.sectionCard, overflow: "hidden" }}>
          <Mui.Box sx={{ p: { xs: 2, md: 2.5 }, borderBottom: `1px solid ${designColors.slate200}` }}>
            <SectionHeading
              icon={<LayersRounded />}
              title="Modules & Permissions"
              subtitle="Select modules and configure sub-module permissions for this role."
              action={twoColumn ? searchField : null}
            />
            {twoColumn ? null : searchField}
          </Mui.Box>
          <Mui.Grid container>
            <Mui.Grid
              item
              xs={12}
              md={twoColumn ? 4.5 : 12}
              lg={twoColumn ? 4 : 12}
              sx={{
                p: 2,
                borderRight: twoColumn ? { md: `1px solid ${designColors.slate200}` } : "none",
                borderBottom: twoColumn
                  ? { xs: `1px solid ${designColors.slate200}`, md: "none" }
                  : `1px solid ${designColors.slate200}`,
              }}
            >
              <Mui.Typography sx={{ ...roleKitType.sectionTitle, mb: 1.25, px: 0.5 }}>
                Modules
              </Mui.Typography>
              {orderedProducts.length === 0 && !catalogDetailsLoading ? (
                <EmptyState
                  size={64}
                  title="No Modules Found"
                  subtitle="No modules are configured for this company yet."
                  sx={{ py: 2 }}
                />
              ) : (
                <ModuleList
                  products={orderedProducts}
                  activeProduct={activeProduct}
                  onSelect={setActiveProduct}
                  onToggle={toggleProduct}
                  footer={
                    catalogDetailsLoading ? (
                      <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.25, py: 1 }}>
                        <Mui.CircularProgress size={16} sx={{ color: "#0D9488" }} />
                        <Mui.Typography sx={{ fontSize: 13, color: designColors.slate600 }}>
                          Loading modules...
                        </Mui.Typography>
                      </Mui.Stack>
                    ) : null
                  }
                />
              )}
              {moduleError ? (
                <Mui.Typography sx={{ mt: 1, px: 0.5, fontSize: 13, color: "#D32F2F" }}>
                  {moduleError}
                </Mui.Typography>
              ) : null}
            </Mui.Grid>
            <Mui.Grid item xs={12} md={twoColumn ? 7.5 : 12} lg={twoColumn ? 8 : 12} sx={{ p: 2, minWidth: 0 }}>
              {selectedProduct ? (
                <>
                  <Mui.Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                  >
                    <Mui.Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                      <ProductIcon name={selectedProduct?.product_name} size={44} />
                      <Mui.Box sx={{ minWidth: 0 }}>
                        <Mui.Typography sx={roleKitType.sectionTitle}>
                          Permissions for {selectedProduct?.product_name}
                        </Mui.Typography>
                        <Mui.Typography sx={roleKitType.sectionSubtitle}>
                          Configure permissions for sub-modules under {selectedProduct?.product_name}.
                        </Mui.Typography>
                      </Mui.Box>
                    </Mui.Stack>
                    {(() => {
                      const productName = selectedProduct?.product_name;
                      const submodules = (selectedProduct?.module_details ?? []).flatMap(
                        (module) => module?.submodules ?? [],
                      );
                      const fullCount = submodules.filter(isFullyGranted).length;
                      const allSelected = submodules.length > 0 && fullCount === submodules.length;
                      const someSelected = submodules.some(isSubmoduleEnabled);
                      return (
                        <Mui.ButtonBase
                          sx={{ ...roleAndGroupUi.inlineActionButton, flexShrink: 0 }}
                          disabled={submodules.length === 0}
                          onClick={() => setAllPermissions(productName, !allSelected)}
                        >
                          <Mui.Checkbox
                            checked={allSelected}
                            indeterminate={!allSelected && someSelected}
                            tabIndex={-1}
                            size="small"
                            sx={{ ...tealCheckboxSx, p: 0, mr: 0.5, pointerEvents: "none" }}
                            inputProps={{ "aria-hidden": true }}
                          />
                          {allSelected ? "Deselect All" : "Select All"}
                        </Mui.ButtonBase>
                      );
                    })()}
                  </Mui.Stack>
                  <PermissionMatrix
                    product={selectedProduct}
                    search={search}
                    onToggleAll={(value) => toggleProduct(selectedProduct?.product_name, value)}
                    onToggleSubmodule={toggleSubmodule}
                    onPermissionChange={changePermission}
                    onColumnChange={(field, value) =>
                      setColumnPermission(selectedProduct?.product_name, field, value)
                    }
                    wide={twoColumn}
                  />
                  {approvalRequiresEdit(selectedProduct?.product_name) ? (
                    <Mui.Typography sx={{ mt: 1.25, fontSize: 12.5, color: designColors.slate600 }}>
                      Approve also grants Edit for sub-modules in this module.
                    </Mui.Typography>
                  ) : null}
                </>
              ) : (
                <EmptyState
                  title="No Module Selected"
                  subtitle={`Select a module ${twoColumn ? "on the left" : "above"} to configure its permissions.`}
                />
              )}
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Box>
      </Mui.Stack>
  );

  const footerRow = (
        <Mui.Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          {isEdit && canEdit ? (
            <Mui.Stack direction="row" spacing={1} alignItems="center">
              {hasUnsavedChanges ? (
                <ErrorOutlineOutlined sx={{ fontSize: 20, color: "#F97316" }} />
              ) : (
                <CheckCircleRounded sx={{ fontSize: 20, color: "#0F766E" }} />
              )}
              <Mui.Typography
                sx={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  fontFamily: designFontFamily,
                  color: hasUnsavedChanges ? "#C2410C" : "#0F766E",
                }}
              >
                {hasUnsavedChanges ? "Unsaved changes" : "All changes saved"}
              </Mui.Typography>
            </Mui.Stack>
          ) : (
            <Mui.Box />
          )}
          <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Mui.Button sx={roleAndGroupUi.secondaryButton} onClick={goBack}>
              Cancel
            </Mui.Button>
            {canEdit ? (
              <Mui.Button
                    startIcon={
                      isSaving ? (
                        <Mui.CircularProgress size={18} color="inherit" />
                      ) : (
                        <SaveOutlined fontSize="small" />
                      )
                    }
                    sx={roleAndGroupUi.primaryButton}
                    onClick={saveRole}
                    disabled={isSaving || !hasUnsavedChanges}
                    aria-busy={isSaving}
                  >
                    {isSaving ? "Saving..." : isEdit ? "Save Changes" : "Create Role"}
                  </Mui.Button>
            ) : null}
          </Mui.Stack>
        </Mui.Stack>
  );

  if (isPanel) {
    return (
      <GlobalSidePanel
        open
        onClose={goBack}
        expanded={expanded}
        onToggleExpand={() => setExpanded((prev) => !prev)}
        title={title}
        subtitle={subtitle}
        breadcrumb={breadcrumbTrail}
        footer={footerRow}
        bodySx={{ backgroundColor: designColors.pageBg }}
      >
        <Mui.Box sx={{ p: { xs: 2, md: expanded ? 2.5 : 2 } }}>
          {dataReady ? (
            body
          ) : (
            <Loader info="Loading..." minHeight="60vh" />
          )}
        </Mui.Box>
      </GlobalSidePanel>
    );
  }

  return (
    <Mui.Box sx={{ ...roleAndGroupUi.page, pb: 0 }}>
      <GlobalBreadcrumb trail={breadcrumbTrail} />
      <Mui.Typography sx={{ ...roleAndGroupUi.title, mt: 0.5 }}>{title}</Mui.Typography>
      <Mui.Typography sx={{ ...roleAndGroupUi.subtitle, mb: 2.5 }}>{subtitle}</Mui.Typography>
      {body}
      <Mui.Box sx={{ ...roleAndGroupUi.stickyFooter, mx: { xs: -1, md: -2 }, mt: 2.5 }}>
        {footerRow}
      </Mui.Box>
    </Mui.Box>
  );
};
